import { deleteField, doc, onSnapshot, setDoc, type DocumentData, type WithFieldValue } from "firebase/firestore"
import { get, type Writable, writable } from "svelte/store"
import { registerUserStatusCallback } from "./helpers/unload-promise"
import { logWrite } from "./log"
import { db, getUserProfileDocPath, userStatus } from "./underhood/firebase"
import { clone } from "lodash-es"
import { delta } from "./underhood/lodash-delta"

/**
 *
 * @param key
 * @returns null if none
 */
function getLocal<T>(key: string): T | null {
    const item = localStorage.getItem(key)
    if (!item) return null
    return JSON.parse(item) as T
}

function setLocal<T>(key: string, obj: T): void {
    localStorage.setItem(key, JSON.stringify(obj))
}

function getLocalOrSetDefault<T>(key: string, def: T): T {
    const local = getLocal<T>(key)
    if (local) {
        return local
    } else {
        setLocal<T>(key, def)
        return getLocal<T>(key) as T // This prevents any kind of future accidental changing of def
    }
}

function transferLocaltoServer<T extends WithFieldValue<DocumentData>>(key: string, document: ReturnType<typeof doc>, def: T) {
    const local = getLocalOrSetDefault(key, def)
    logWrite(document.path, local)
    const doc = setDoc(document, local)
    doc.then(() => {
        // Check if changed first
        localStorage.removeItem(key)
    })
    return doc
}

function getDocument(collection: string, key: string) {
    const docPath = getUserProfileDocPath() + "/" + collection + "/" + key
    return doc(db, docPath)
}

function safeObject<T extends object>(o: T): T {
    return JSON.parse(JSON.stringify(o))
}

type Settings<T> = {
    name: string
    version: number
    def: T
    metadataListener?: MetadataListener<T>
    collection?: string // default: "data"
    ready?: Writable<boolean>
    reset?: () => void

    /**
     * `noLocal` makes it so that nothing is stored locally. Instead, the default
     * object is loaded at the start, and any changes are saved only to the transient
     * store in the session, unless connected. If using noLocal, please ensure
     * in your own way that writes are done on the server prior to calling `set` or `setMerge`
     */
    noLocal?: boolean // default: false
}

/**
 * Gets all information for a particular write
 * False actually blocks it from writing to the cacheStore ? Maybe. Not implemented yet
 *
 * Return a function that will be called after the store has been written!
 */
export type MetadataListener<T> = (
    flags: {
        /**
         * Firestore's hasPendingWrites
         */
        hasPendingWrites: boolean
        /**
         * Firestore's fromCache
         */
        fromCache: boolean
        /**
         * When the user first logs in, this is the first read
         */
        firstReadOfSession: boolean
        /**
         * If there is no document and it needs to be created, this is the
         * first write.
         */
        firstWriteEver: boolean
    },
    data: Partial<T>
) => (void | (() => void))

/**
 *
 * The idea with this function is to return a store that automatically
 * handles all the functionality in terms of
 * 1. Storing locally
 * 2. Storing remote (if logged in)
 * 3. Transferring remote from local if first time
 *
 * It also adds a {@link registerUserStatusCallback} entry so that the app can safely
 * handle transfer of information through logins and logouts
 *
 * @param name name for firebase document and local storage key
 * @param version version (appended to name for localStorage and firestore key)
 * @param def default
 * @param collection optional collection for firebase
 * @param ready writable store that changes to true when we are loaded with info and false when loading or changing users
 * @param metadataListener intercepts metadata changes
 * @param reset pass a function that's called when the user status changes. Ideally this should be resetting things to factory default, as though you were starting the app fresh
 * @returns a reactive store allowing you to read changes and write changes to the appriopriate API
 */
export function createFirebaseCache<T extends Record<string, unknown>>({
    name,
    version,
    def,
    collection = "data",
    ready,
    metadataListener,
    reset,
    noLocal = false,
}: Settings<T>) {
    // Let's check local storage
    const key = name + "_" + version

    // let setReady : (b : boolean) => void
    // const ready = readable<boolean>(false, set => {setReady = set})
    const store = writable<T | null>(null)
    const setStore = store.set

    let listenerUnsubscribe: (() => void) | null
    registerUserStatusCallback({
        async unload() {
            if (ready) ready.set(false)

            // Reset firestore subscriber
            if (listenerUnsubscribe) {
                listenerUnsubscribe()
                listenerUnsubscribe = null
            }

            // Passed function in case anything needs to be reset while changing to new user data
            if (reset) reset()

            setStore(null) // Changing status
        },

        async load(status) {
            if (status == "out" || status == "unverified") {
                // Working in localStorage
                // Do we have anything stored?
                setStore(noLocal ? safeObject(def) : getLocalOrSetDefault(key, def))
            } else {
                // Verified
                // Check if it exists
                const document = getDocument(collection, key)
                let firstReadOfSession = true
                let firstWriteEver = false
                // let lastDelta : Partial<T> = {}
                // Set up our listener
                await new Promise<void>((loadReady) => {
                    listenerUnsubscribe = onSnapshot(
                        document,
                        { includeMetadataChanges: !!metadataListener },
                        (snapshot) => {
                            if (!snapshot.exists()) {
                                // Does not exist - must be first time - initiate transfer
                                if (noLocal) {
                                    // Set doc immediately if no local
                                    logWrite(document.path, document, "noLocal snapshot does not exist")
                                    setDoc(document, def)
                                } else {
                                    // Otherwise transfer from local to server
                                    transferLocaltoServer(key, document, def)
                                }

                                // Set first write, for next round of snapshot
                                firstWriteEver = true
                                return
                            }

                            const data: T = snapshot.data() as T // Assuming here that it's set properly

                            let potentialPostAssignmentFunction : (() => void) | undefined = undefined
                            // Propagate metadata
                            if (metadataListener) {
                                const { hasPendingWrites, fromCache } = snapshot.metadata

                                // TODO, make it so that if returns false, it disables setting in the store
                                potentialPostAssignmentFunction = metadataListener(
                                    {
                                        hasPendingWrites,
                                        fromCache,
                                        firstReadOfSession,
                                        firstWriteEver,
                                    },
                                    delta<T>(clone(data), get(store)) as Partial<T>
                                ) ?? undefined
                            }
                            firstReadOfSession = false
                            firstWriteEver = false

                            setStore(data)
                            // Ready
                            loadReady()
                            if (potentialPostAssignmentFunction) potentialPostAssignmentFunction()
                        }
                    )
                })
            }

            if (ready) ready.set(true)
        },
    })

    async function set(obj: T) {
        if (get(userStatus) == "verified") {
            // Set to firestore - onSnapshot will automatically update store
            const doc = getDocument(collection, key)
            logWrite(doc.path, obj)
            return setDoc(doc, obj)
        } else {
            // Dealing with localStorage
            if (!noLocal) setLocal(key, obj)
            setStore(obj)
        }
    }

    async function update(updater: (obj: T) => T) {
        const newObj = updater(get(store))
        set(newObj)
    }

    /**
     * Same as set except merges. Implements merge functionality for LS
     */
    async function setMerge(obj: Partial<T>) {
        if (get(userStatus) == "verified") {
            const doc = getDocument(collection, key)
            logWrite(doc.path, obj)
            return setDoc(doc, obj, { merge: true })
        } else {
            const localCopy = getLocal<T>(key)
            const newCopy = { ...localCopy, ...obj }
            if (!noLocal) setLocal(key, newCopy)
            store.set(newCopy)
        }
    }

    /**
     * Deletes item at ID
     */
    async function deleteMerge(id: string) {
        if (get(userStatus) == "verified") {
            const doc = getDocument(collection, key)
            const data = { [id]: deleteField() }
            logWrite(doc.path, data)
            return setDoc(doc, data, { merge: true })
        } else {
            const localCopy = getLocal<T>(key)
            delete localCopy?.[id]
            if (!noLocal) setLocal(key, localCopy)
            store.set(localCopy)
        }
    }

    return {
        subscribe: store.subscribe,
        update,
        set,
        setMerge,
        deleteMerge,
    }
}
